import { Auth } from 'aws-amplify';
import { IResetPasswordDTO } from 'types/stateTypes';
import { Common } from 'utils/constants';
import {
    IAuthResponse,
    IUserData,
    ILoginFormField,
    IForgotPasswordFormField,
    IForgotPasswordChangeField,
    IChangePasswordField,
} from 'models/login';
import { trackPromise } from 'react-promise-tracker';
import StandardResponse from 'models/standard-response';

export default class LoginService {
    getCognitoUser = async (configs: any) => {
        return await trackPromise(Auth.currentAuthenticatedUser(configs));
    };

    login = async (data: ILoginFormField): Promise<StandardResponse<IUserData>> => {
        const res = await trackPromise(Auth.signIn(data.Email.toLowerCase(), data.PassWord));
        return new StandardResponse<IUserData>(res.status, res, res.statusText, null);
    };

    logout = async () => {
        try {
            const data = await trackPromise(Auth.signOut());
            return Promise.resolve(data);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    authUser = async (data: ILoginFormField): Promise<IAuthResponse> => {
        try {
            let response: IAuthResponse;
            const user = await trackPromise(Auth.signIn(data.Email.toLowerCase(), data.PassWord));
            if (user.challengeName === Common.AuthChallengeName.NEW_PASSWORD_REQUIRED) {
                response = {
                    status: Common.AuthChallengeName.NEW_PASSWORD_REQUIRED,
                    user,
                    email: data.Email.toLowerCase(),
                };
            } else if (user.challengeName === Common.AuthChallengeName.MFA_SETUP) {
                const code = await trackPromise(Auth.setupTOTP(user));
                response = { status: Common.AuthChallengeName.MFA_SETUP, user, code, email: data.Email.toLowerCase() };
            } else if (user.challengeName === Common.AuthChallengeName.SOFTWARE_TOKEN_MFA) {
                response = { status: Common.AuthChallengeName.SOFTWARE_TOKEN_MFA, user };
            } else {
                response = { status: Common.AuthChallengeName.SUCCESS, user };
            }
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    forgotPassword = async (data: IForgotPasswordFormField): Promise<IAuthResponse> => {
        try {
            await trackPromise(Auth.forgotPassword(data.Email.toLowerCase()));
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    forgotPasswordChange = async (data: IForgotPasswordChangeField): Promise<IAuthResponse> => {
        try {
            await trackPromise(Auth.forgotPasswordSubmit(data.Email.toLowerCase(), data.Code, data.PassWord));
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    forcePasswordChange = async (data: IResetPasswordDTO): Promise<IAuthResponse> => {
        try {
            let response: IAuthResponse;
            const user = await trackPromise(Auth.completeNewPassword(data.user, data.newPassWord, null));
            // set change password notification
            if (user.challengeName === Common.AuthChallengeName.MFA_SETUP) {
                const code = await trackPromise(Auth.setupTOTP(user));
                response = { status: Common.AuthChallengeName.MFA_SETUP, user, code };
            } else if (user.challengeName === Common.AuthChallengeName.SOFTWARE_TOKEN_MFA) {
                response = { status: Common.AuthChallengeName.SOFTWARE_TOKEN_MFA, user };
            } else {
                response = { status: Common.AuthChallengeName.SUCCESS, user };
            }
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    authVerifyCodeTotp = async (user: any, code: string): Promise<IAuthResponse> => {
        try {
            await Auth.verifyTotpToken(user, code)
                .then(async () => {})
                .catch((e) => {
                    Promise.reject(e);
                });

            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS, user });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    verifyCodeOTP = async (user: any, code: string): Promise<IAuthResponse> => {
        try {
            user = await trackPromise(Auth.confirmSignIn(user, code, Common.AuthChallengeName.SOFTWARE_TOKEN_MFA));
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS, user });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    changePassword = async (data: IChangePasswordField): Promise<object> => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            await trackPromise(Auth.changePassword(user, data.CurrentPassword, data.NewPassword));
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getCurrentMFAType = async (): Promise<any> => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            const result = await Auth.getPreferredMFA(user, { bypassCache: true });
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS, data: result });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    setMFAAuthentication = async (isMFA: boolean): Promise<any> => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            let result = null;
            if (isMFA) {
                result = await Auth.setupTOTP(user);
            } else {
                result = await Auth.setPreferredMFA(user, Common.Auth.NOMFA);
            }
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS, data: result });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    setMFASecurityCodeVerify = async (code: string): Promise<any> => {
        try {
            const user = await trackPromise(Auth.currentAuthenticatedUser());
            await trackPromise(Auth.verifyTotpToken(user, code));
            await trackPromise(Auth.setPreferredMFA(user, 'TOTP'));
            return Promise.resolve({ status: Common.AuthChallengeName.SUCCESS });
        } catch (error) {
            return Promise.reject(error);
        }
    };
}
